import React, { useEffect, useMemo, useRef, useState } from 'react';
import { findBestMatch } from 'string-similarity';
import { ValidationState } from '../../types';
import { normalizeLabel } from '../../utils/normalizeLabel';
import { CaptureOuterClick } from '../CaptureOuterClick';

import './styles.css';

const HEIGHT = 32;

type ItemProps = React.HTMLAttributes<HTMLDivElement> & {
	active: boolean;
	onClick: () => void;
};

const Item: React.FC<ItemProps> = ({
	active,
	children,
	onClick,
	className,
	...props
}) => (
	<div
		onClick={onClick}
		onKeyUp={e => {
			if (e.keyCode === 13) {
				e.stopPropagation();

				onClick();
			}
		}}
		className={`filter-select-item py-1 px-2 d-block text-capitalize${
			active
				? ' text-primary bg-primary-transparent'
				: ' text-dark-hover-primary bg-primary-hover-transparent cursor-pointer'
		}${className ? ` ${className}` : ''}`}
		{...props}
	>
		{children}
	</div>
);

export type FilterSelectProps = React.HTMLAttributes<HTMLDivElement> & {
	label: string;
	placeHolder?: string;
	items: string[];
	setIndex: (index: number) => void;
	initialIndex?: number;
	tabIndex?: number;
	validation?: ValidationState;
};

type List = [item: string, index: number];
type ListIndices = { [item: string]: number };

export const FilterSelect: React.FC<FilterSelectProps> = ({
	label,
	placeHolder = label,
	items,
	setIndex,
	initialIndex = -1,
	className,
	tabIndex,
	validation: validationState,
	...props
}) => {
	const { validation, ignoreTouched } = validationState ?? {};

	const normalizedLabel = useMemo(() => normalizeLabel(label), [label]);
	const itemsList: List[] = useMemo(
		() => items.map((item, index) => [item, index]),
		[items]
	);
	const itemsIndices: ListIndices = useMemo(() => {
		const output: ListIndices = {};

		for (const [item, index] of itemsList) {
			output[item] = index;
		}

		return output;
	}, [itemsList]);

	const [focus, setFocus] = useState(false);
	const [value, setValue] = useState(
		initialIndex in items ? items[initialIndex] : ''
	);
	const inputRef = useRef<HTMLInputElement>(null);
	// const [inputHeightRef, { height: inputHeight }] =
	// 	useMeasure<HTMLInputElement>();

	const [filteredList, selected] = useMemo(() => {
		if (value === '') {
			return [[], -1];
		}

		const { ratings, bestMatch, bestMatchIndex } = findBestMatch(
			value.toLowerCase(),
			items
		);

		return [
			focus
				? ratings
						.filter(({ rating }) => rating > 0)
						.sort((a, b) => b.rating - a.rating)
						.filter((_, index) => index <= 10)
						.map(({ target }) => ({
							item: target,
							key: itemsIndices[target]
						}))
				: [],
			bestMatch.rating > 0 ? bestMatchIndex : -1
		];
	}, [value, items, itemsIndices, focus]);

	useEffect(() => setIndex(selected), [selected, setIndex]);
	useEffect(() => {
		if (!focus && inputRef.current) {
			inputRef.current.blur();
		}
	}, [focus, inputRef]);

	const height = filteredList.length > 0 ? filteredList.length * HEIGHT : 0;

	const blurHandler: React.FocusEventHandler<
		HTMLDivElement | HTMLInputElement
	> = e => {
		const containsNode = optionsRef.current?.contains(
			e.relatedTarget as Node
		);

		if (!containsNode) {
			setFocus(false);
		}
	};

	const optionsRef = useRef<HTMLDivElement>(null);

	return (
		<div
			className={`form-group filter-select position-relative ${
				className ? ` ${className}` : ''
			}`}
			{...props}
		>
			{label !== '' && (
				<label
					className="input-label p-0 mb-1 mr-3"
					htmlFor={normalizedLabel}
				>
					{label}
				</label>
			)}

			<CaptureOuterClick
				onMouseDown={() => {
					if (selected === -1) {
						setValue('');
					} else {
						setValue(items[selected]);
					}

					setFocus(false);
				}}
				containerRef={optionsRef}
			>
				<div
					className="position-relative"
					style={{ zIndex: 999, display: 'inline-block' }}
				>
					<input
						ref={inputRef}
						type="text"
						className="form-control px-3 m-0 text-capitalize filter-select-input"
						id={normalizedLabel}
						value={value}
						tabIndex={tabIndex}
						placeholder={placeHolder}
						onChange={e => {
							setValue(e.target.value.toLowerCase());
						}}
						onFocus={() => setFocus(true)}
						onBlur={blurHandler}
						autoComplete="off"
					/>
				</div>
				<div
					style={{
						top: '100%',
						left: 0,
						right: 0,
						overflow: 'hidden',
						zIndex: 1000,
						height,
						transition: 'height 1s ease, opacity 1s ease',
						opacity: filteredList.length > 0 ? 1 : 0
					}}
					className={`box-with-verticle-spring position-relative filter-select-options border-top-0 position-absolute bg-white w-100`}
				>
					{filteredList.map(filtered => (
						<div
							className="w-100"
							style={{ height: HEIGHT }}
							key={filtered.key}
						>
							<Item
								onClick={() => {
									setValue(filtered.item);
									setFocus(false);
								}}
								active={selected === filtered.key}
								onBlur={blurHandler}
								tabIndex={focus ? 0 : -1}
								data-name={normalizeLabel(filtered.item)} // useful for e2e testing
							>
								{filtered.item}
							</Item>
						</div>
					))}
				</div>
			</CaptureOuterClick>

			{validation && ignoreTouched && (
				<div className="text-danger">{validation}</div>
			)}
		</div>
	);
};
